import { ITheme } from '../interfaces/i-theme';
import { INeutralThemeColors } from '../interfaces/i-neutral-theme-colors';
import { ThemeOptionEnum } from '../enum/theme-option-enum';
import { VMCORE_BRAND } from '../constants/vmcore-brand';

export const DEFAULT_PRIMARY_COLOR = VMCORE_BRAND.primary;

function isHexColor(color?: string | null): color is string {
  return Boolean(color && /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(color));
}

function normalizeHexColor(color: string): string {
  if (color.length === 4) {
    return `#${color
      .slice(1)
      .split('')
      .map((digit) => `${digit}${digit}`)
      .join('')}`.toUpperCase();
  }

  return color.toUpperCase();
}

function darkenHexColor(color: string, amount: number): string {
  const normalized = normalizeHexColor(color).slice(1);
  const channels = normalized.match(/.{1,2}/g);

  if (!channels) {
    return color;
  }

  const darkened = channels.map((channel) => {
    const decimal = parseInt(channel, 16);
    const nextValue = Math.max(0, Math.round(decimal * (1 - amount)));

    return nextValue.toString(16).padStart(2, '0');
  });

  return `#${darkened.join('')}`.toUpperCase();
}

export function resolveThemePrimaryColor(color?: string | null): string {
  if (!isHexColor(color)) {
    return DEFAULT_PRIMARY_COLOR;
  }

  return normalizeHexColor(color);
}

const lightNeutralColors: INeutralThemeColors = {
  '1000': '#FFFFFF',
  '950': '#F2F5F8',
  '900': '#E1E4EA',
  '800': '#CACFD8',
  '700': '#99A0AE',
  '600': '#717784',
  '500': '#525866',
  '400': '#2B303B',
  '300': '#222530',
  '200': '#181B25',
  '100': '#0E121B',
  '50': '#000000',
  '0': '#000000',
};

const darkNeutralColors: INeutralThemeColors = {
  '1000': '#000000',
  '950': '#0E121B',
  '900': '#181B25',
  '800': '#222530',
  '700': '#2B303B',
  '600': '#525866',
  '500': '#717784',
  '400': '#99A0AE',
  '300': '#CACFD8',
  '200': '#E1E4EA',
  '100': '#F2F5F8',
  '50': '#F5F7FA',
  '0': '#FFFFFF',
};

export const dynamicTheme = (
  option: ThemeOptionEnum,
  primaryColor?: string | null
): ITheme => {
  const primary = resolveThemePrimaryColor(primaryColor);
  const isVmcore = !primaryColor;

  return {
    primary,
    primary_header: isVmcore ? VMCORE_BRAND.navy : darkenHexColor(primary, 0.3),
    primary_darker: isVmcore
      ? VMCORE_BRAND.navy
      : darkenHexColor(primary, 0.62),
    red: '#fb3748',
    green: '#1FC16B',
    yellow: '#f6b51e',
    orange: '#ff8447',
    transparent: 'rgba(0,0,0,0)',
    neutral:
      option === ThemeOptionEnum.light
        ? {
            ...lightNeutralColors,
            ...(isVmcore && {
              '950': VMCORE_BRAND.pale,
              '900': '#D9E5F7',
              '100': VMCORE_BRAND.navy,
            }),
          }
        : {
            ...darkNeutralColors,
            ...(isVmcore && {
              '950': VMCORE_BRAND.navy,
              '900': '#102647',
              '100': VMCORE_BRAND.pale,
            }),
          },
  };
};
